import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star } from "lucide-react";

const RatingPrompt = ({ delivery, role, onDone }) => {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  // Auto-open once per delivery when it's delivered and this role hasn't rated yet.
  useEffect(() => {
    if (!delivery) return;
    if (delivery.status !== "delivered") return;
    const flag = role === "sender" ? delivery.rated_by_sender : delivery.rated_by_carrier;
    // legacy data: sender may have "rated" without the per-role field
    const legacyRated = role === "sender" ? delivery.rated : false;
    if (flag || legacyRated) return;
    const seenKey = `cit_rated_${role}_${delivery.id}`;
    if (sessionStorage.getItem(seenKey)) return;
    setOpen(true);
  }, [delivery?.id, delivery?.status, role, delivery?.rated_by_sender, delivery?.rated_by_carrier, delivery?.rated]);

  const dismiss = () => {
    if (delivery?.id) sessionStorage.setItem(`cit_rated_${role}_${delivery.id}`, "1");
    setOpen(false);
  };

  const submit = async () => {
    if (!stars) { toast.error("Pick a star rating."); return; }
    setBusy(true);
    try {
      await api.post("/ratings", {
        delivery_id: delivery.id,
        stars,
        comment: comment.trim(),
      });
      toast.success("Thanks for the feedback.");
      dismiss();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the rating.");
    } finally { setBusy(false); }
  };

  const title = role === "sender" ? "How was your carrier?" : "How was the sender?";
  const sub = role === "sender"
    ? "Your rating helps the next sender choose the right carrier."
    : "Your rating helps the next carrier decide which parcels to accept.";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); else setOpen(true); }}>
      <DialogContent data-testid="rating-prompt-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{sub}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 justify-center py-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              data-testid={`rating-star-${n}`}
              onClick={() => setStars(n)}
              className="transition-transform hover:scale-110"
              aria-label={`${n} stars`}
            >
              <Star
                className={`w-10 h-10 ${n <= stars ? "fill-amber-400 stroke-amber-500" : "stroke-slate-300"}`}
              />
            </button>
          ))}
        </div>
        <Textarea
          data-testid="rating-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a short note (optional)"
          rows={3}
          maxLength={300}
        />
        <DialogFooter>
          <Button variant="outline" onClick={dismiss}>Later</Button>
          <Button
            onClick={submit}
            disabled={busy || !stars}
            data-testid="rating-submit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy ? "Saving…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RatingPrompt;
